import { useState, type ReactNode } from 'react';
import { Button, Card, StatusDot } from '@paige/ui';
import { useFormat } from '@paige/format';
import { FlowCardsStrip } from './FlowCardsStrip';
import type { FlowLinks } from './FlowDetail';
import { FlowIncidentDrawer, type DrawerViewer } from './FlowIncidentDrawer';
import { FlowVerdict } from './FlowVerdict';
import { starvedPlaces, withIncidents } from './flow-verdict';
import type { IncidentLoaders } from './IncidentWho';
import type { ProblemActRequest, ProblemActResult } from '../problems/problem-types';
import { FlowGlyph } from './FlowGlyph';
import { FlowMap } from './FlowMap';
import { useFlowPeek } from './FlowPeek';
import { FlowScrubber } from './FlowScrubber';
import { useFlowBursts } from './FlowTrack';
import { pastStatus, placeAt, type FlowHistory } from './flow-history';
import { engineStatusOf, pulseOf, statusOf, type FlowStatus } from './flow-state';
import type {
  AdminFlow,
  FlowIncident,
  FlowItemsPage,
  FlowItemsQuery,
  FlowPlace,
} from './flow-types';
import { COST_ESTIMATE_EXPLAINED, dollarsAPage } from '../revenue/CostEstimate';
import { CompanyPicker, type CompanyPickerProps } from '../CompanyPicker';
import './flow.css';
import './flow-more.css';

/** The last hour: read only while the scrubber is open (the page asks once a minute then). */
export interface BoardHistory {
  open: boolean;
  onOpen: (open: boolean) => void;
  data: FlowHistory | null;
  failed: string | null;
}

export interface FlowBoardProps {
  flow: AdminFlow;
  /** Drill to one company by slug, or back to every company with null. */
  onCompany: (slug: string | null) => void;
  links: FlowLinks;
  /** Reads the items behind a count (`GET /admin/flow/items`); a stage's detail lists them. */
  loadItems?: ((q: FlowItemsQuery) => Promise<FlowItemsPage>) | undefined;
  /** The last read failed: the picture is the last one that arrived, and says so. */
  stale?: boolean | undefined;
  /** The company picker's search (`GET /admin/companies?q=`); without it the scope is shown, not changed. */
  searchCompanies?: CompanyPickerProps['search'] | undefined;
  /** What is not simply flowing (`GET /admin/flow/incidents`); null until first read. */
  incidents: FlowIncident[] | null;
  /** The stage whose drawer is open, and the incident open in it: from the address, so a view can be sent. */
  opened: { place: FlowPlace; incident: string | null } | null;
  onOpen: (opened: { place: FlowPlace; incident: string | null } | null) => void;
  loaders: IncidentLoaders;
  /** Does a staff action; absent for a role the API refuses. */
  onAct?: ((request: ProblemActRequest) => Promise<ProblemActResult>) | undefined;
  onViewAsCustomer?: ((company: { id: string; name: string }, reason: string) => void) | undefined;
  /** The support viewer inside the drawer; absent for a role that may not open a company's files. */
  viewer?: DrawerViewer | undefined;
  /** The last hour's scrubber; absent where the page offers none. */
  history?: BoardHistory | undefined;
  /** The page is asking (not paused, tab shown): hover previews ask only then. */
  live?: boolean | undefined;
}

/** Today so far (UTC): pages and files in, when a document last went out, and what a page read cost to process. */
function Today({ flow }: { flow: AdminFlow }): ReactNode {
  const f = useFormat();
  return (
    <p className="fl-today">
      <span>
        <strong>{f.int(flow.pagesToday)}</strong> pages in {f.int(flow.filesToday)}{' '}
        {flow.filesToday === 1 ? 'file' : 'files'} today
      </span>
      <span>
        last document out{' '}
        <strong>
          {flow.lastFileAt ? f.ago(flow.lastFileAt, Date.parse(flow.asOf)) : 'none yet today'}
        </strong>
      </span>
      <span title={`Today (UTC). ${COST_ESTIMATE_EXPLAINED}`}>
        <strong>{dollarsAPage(f, flow.costPerPageToday)}</strong> processing cost a page
      </span>
    </p>
  );
}

/** Said in place of "Live" when the screen could not ask: nothing on it is moving because nothing is known. */
function Stale({ flow }: { flow: AdminFlow }): ReactNode {
  const f = useFormat();
  return (
    <p className="fl-pulse fl-pulse--stale" data-pulse="stale" role="status">
      <FlowGlyph tone="slow" />
      <strong>Not updating</strong>
      <span>Paige did not answer; this is the picture from {f.time(flow.asOf)}</span>
    </p>
  );
}

/** "Live" with what is arriving and leaving, or "Nothing in flight" with when anything last moved. */
function Pulse({ flow }: { flow: AdminFlow }): ReactNode {
  const f = useFormat();
  const pulse = pulseOf(flow);
  if (!pulse.live) {
    return (
      <p className="fl-pulse fl-pulse--calm" data-pulse="calm">
        <FlowGlyph tone="idle" />
        <strong>Nothing in flight</strong>
        <span>
          {flow.lastActivityAt
            ? `last activity ${f.ago(flow.lastActivityAt, Date.parse(flow.asOf))}`
            : 'nothing has arrived yet'}
        </span>
      </p>
    );
  }
  return (
    <p className="fl-pulse" data-pulse="live">
      <StatusDot tone="accent" live label="Live" />
      <strong>Live</strong>
      <span>
        {f.int(pulse.arriving)} pages/min arriving, {f.int(pulse.leaving)} leaving,{' '}
        {f.int(pulse.working)} being worked
      </span>
    </p>
  );
}

/**
 * Whose files the picture shows: the admin area's company picker, which asks the server as you type. Never a
 * list of every company (Dustin, 2026-10-07: "imagine we have a thousand customers").
 */
function Scope({
  flow,
  onCompany,
  search,
}: {
  flow: AdminFlow;
  onCompany: (slug: string | null) => void;
  search: FlowBoardProps['searchCompanies'];
}): ReactNode {
  const chosen = flow.company ? { id: flow.company.slug, name: flow.company.name } : null;
  if (!search) return <span className="fl-scope">{chosen?.name ?? 'All companies'}</span>;
  return (
    <span className="fl-scope">
      <CompanyPicker value={chosen} search={search} onChange={(c) => onCompany(c?.id ?? null)} />
    </span>
  );
}

/** The drawer of the stage the address names. */
function Drawer(
  props: FlowBoardProps & { opened: NonNullable<FlowBoardProps['opened']>; status: FlowStatus },
): ReactNode {
  const { opened, onOpen } = props;
  return (
    <FlowIncidentDrawer
      key={opened.place}
      flow={props.flow}
      place={opened.place}
      status={props.status}
      incidents={props.incidents ?? []}
      incidentId={opened.incident}
      onIncident={(incident) => onOpen({ place: opened.place, incident })}
      onClose={() => onOpen(null)}
      links={props.links}
      loaders={props.loaders}
      loadItems={props.loadItems}
      onAct={props.onAct}
      onViewAsCustomer={props.onViewAsCustomer}
      viewer={props.viewer}
    />
  );
}

/** "The last hour" opens the scrubber; the scrubber itself when open. */
function LastHour({
  history,
  at,
  onAt,
}: {
  history: BoardHistory | undefined;
  at: number | null;
  onAt: (i: number | null) => void;
}): ReactNode {
  if (!history) return null;
  if (!history.open)
    return (
      <Button
        size="sm"
        variant="secondary"
        className="fl-hour"
        onClick={() => history.onOpen(true)}
      >
        The last hour
      </Button>
    );
  return (
    <FlowScrubber
      history={history.data}
      failed={history.failed}
      at={at}
      onAt={onAt}
      onClose={() => {
        onAt(null);
        history.onOpen(false);
      }}
    />
  );
}

/** A place's status now, judged with the incidents, or as a recorded minute knew it. */
function useStatus(props: FlowBoardProps, past: ReturnType<typeof pastOf>) {
  const { flow, incidents } = props;
  return (place: FlowPlace): FlowStatus => {
    if (past) return pastStatus(placeAt(past, place));
    const cell = flow.cells.find((c) => c.place === place)!;
    const own = place === 'engine' ? engineStatusOf(cell, flow.runs) : statusOf(cell);
    return incidents ? withIncidents(place, own, incidents, flow) : own;
  };
}

const pastOf = (history: BoardHistory | undefined, at: number | null) =>
  history?.open && at !== null ? (history.data?.minutes[at] ?? null) : null;

/**
 * Stages and queues: one picture of documents moving through Paige on both reading roads, the stage a click
 * opens in detail, and the cards under it. Everything drawn is a count or a movement the API reported, or, with
 * the scrubber, a minute paige-watch recorded.
 */
/** The picture's card: what is live, today, whose, the last hour, and the map now or as a minute was. */
function Picture(
  props: FlowBoardProps & { lit: Set<string>; at: number | null; onAt: (i: number | null) => void },
): ReactNode {
  const { flow, onCompany, stale, searchCompanies, opened, onOpen, history, at } = props;
  const bursts = useFlowBursts(flow);
  const past = pastOf(history, at);
  const status = useStatus(props, past);
  const live = props.live !== false && !past;
  const { peek, enter, leave } = useFlowPeek(props.loadItems, flow.company?.slug ?? null, live);
  const open = opened?.place ?? null;
  return (
    <Card className="fl-flow">
      <div className="fl-top">
        {stale ? <Stale flow={flow} /> : <Pulse flow={flow} />}
        <Today flow={flow} />
        <Scope flow={flow} onCompany={onCompany} search={searchCompanies} />
      </div>
      <LastHour history={history} at={at} onAt={props.onAt} />
      <FlowMap
        flow={flow}
        bursts={bursts}
        status={status}
        open={open}
        toggle={(p) => onOpen(open === p ? null : { place: p, incident: null })}
        past={past}
        starved={starvedPlaces(flow)}
        lit={props.lit}
        peek={peek}
        onPeek={(place, on) => (on ? enter(place) : leave())}
      />
    </Card>
  );
}

export function FlowBoard(props: FlowBoardProps): ReactNode {
  const { flow, incidents, opened, onOpen, history } = props;
  const [at, setAt] = useState<number | null>(null);
  const [lit, setLit] = useState<Set<string>>(new Set());
  const past = pastOf(history, at);
  const status = useStatus(props, past);
  return (
    <div className={past ? 'fl-board fl-board--past' : 'fl-board'}>
      <FlowVerdict
        flow={flow}
        incidents={incidents}
        onOpen={(place, incident) => onOpen({ place, incident })}
      />
      <Picture {...props} lit={lit} at={at} onAt={setAt} />
      <FlowCardsStrip
        cards={flow.cards}
        host={flow.engineHost}
        parts={flow.partsStale ? [] : flow.parts}
        Link={props.links.Link}
        testsHidden={flow.testsHidden}
        onLight={(places) => setLit(new Set(places))}
      />
      {opened ? <Drawer {...props} opened={opened} status={status(opened.place)} /> : null}
    </div>
  );
}
